import React from "react";
import axios from "axios";
import { useEffect, useState } from "react";
import { Button, Card, Col, Row, Table } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Pagination from "../../Pagination";
import "./HrDashboard.css";

const AIJobsByJobbox = () => {
  const BASE_API_URL = process.env.REACT_APP_API_URL;

  const location = useLocation();
   const navigate = useNavigate();

  const userName = location.state?.userName;
  const userEmail = location.state?.userEmail;

  const [jobs, setJobs] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(6);
  const [totalPages, setTotalPages] = useState(0);

  const [sortedColumn, setSortedColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState(" ");

  const [loading, setLoading] = useState(false);

  const [selectedJobSummary, setSelectedJobSummary] = useState(null);

  // Fetch AI Jobs created by Jobbox
  const fetchAIJobs = async () => {
    try {
      setLoading(true);

      const params = {
        userEmail: userEmail,
        page: page,
        size: pageSize,
        sortBy: sortedColumn,
        sortOrder: sortOrder,
      };

      const response = await axios.get(`${BASE_API_URL}/getAIJobsByJobbox`, {
        params,
      });

      setJobs(response.data.content);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching AI Jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (column) => {
    let order = "asc";

    if (sortedColumn === column) {
      order = sortOrder === "asc" ? "desc" : "asc";
    }

    setSortedColumn(column);
    setSortOrder(order);
    setPage(0);
  };

  // Fetch jobs whenever filters change
  useEffect(() => {
    fetchAIJobs();
  }, [userEmail, page, pageSize, sortedColumn, sortOrder]);

  // View job summary
  const handleViewSummary = (summary) => {
    setSelectedJobSummary(summary);
  };

  // Close summary modal
  const handleCloseModal = () => {
    setSelectedJobSummary(null);
  };

  // Handle page size
  const handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value);

    setPageSize(size);
    setPage(0);
  };

  // Handle pagination
  const handlePageClick = (data) => {
    const selectedPage = data.selected;

    setPage(selectedPage);
  };

  const isLastPage = page === totalPages - 1;
  const isPageSizeDisabled = isLastPage;

  // Share job
  const handleShare = (job) => {
    const slug = `${job.jobId}-${job.jobTitle
      .replace(/\s+/g, "-")
      .toLowerCase()}-${job.companyName.replace(/\s+/g, "-").toLowerCase()}`;

    const shareUrl = `${window.location.origin}/browse-jobs/job-details/${slug}`;

    // Mobile / supported browsers
    if (navigator.share) {
      navigator
        .share({
          title: "Job Opportunity",
          text: "Check out this job!",
          url: shareUrl,
        })
        .then(() => {
          console.log("Job shared successfully");
        })
        .catch((error) => {
          console.error("Error sharing", error);
        });
    }

    // Desktop / unsupported browsers
    else {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          Swal.fire({
            title: "Link Copied!",
            text: "Job link copied to clipboard. You can share it now.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        })
        .catch(() => {
          Swal.fire({
            title: "Error",
            text: "Could not copy link. Please try again.",
            icon: "error",
          });
        });
    }
  };

  return (
    <div>
      {/* Header */}
      <Row>
        <Col
          md={12}
          className="d-flex justify-content-between align-items-center"
        >
          <div>
            <h2 className="text-start">AI Jobs by Jobbox</h2>

            <p style={{ color: "black" }}>
              Jobs automatically created and managed by Jobbox AI.
            </p>
          </div>
        </Col>
      </Row>

      {/* Loading */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center">
          <div className="spinner-bubble spinner-bubble-primary m-5" />

          <span>Loading...</span>
        </div>
      ) : jobs.length > 0 ? (
        <div>
          <Card body>
            <div className="table-details-list table-wrapper">
              <Table hover responsive>
                <thead>
                  <tr>
                    {/* Job Title */}
                    <th
                      scope="col"
                      onClick={() => handleSort("jobTitle")}
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      Job Title
                      <span>
                        <span
                          style={{
                            color:
                              sortedColumn === "jobTitle" && sortOrder === "asc"
                                ? "black"
                                : "gray",
                          }}
                        >
                          ↑
                        </span>{" "}
                        <span
                          style={{
                            color:
                              sortedColumn === "jobTitle" &&
                              sortOrder === "desc"
                                ? "black"
                                : "gray",
                          }}
                        >
                          ↓
                        </span>
                      </span>
                    </th>

                    {/* Job Type */}
                    <th
                      scope="col"
                      onClick={() => handleSort("jobType")}
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      Job Type
                      <span>
                        <span
                          style={{
                            color:
                              sortedColumn === "jobType" && sortOrder === "asc"
                                ? "black"
                                : "gray",
                          }}
                        >
                          ↑
                        </span>{" "}
                        <span
                          style={{
                            color:
                              sortedColumn === "jobType" && sortOrder === "desc"
                                ? "black"
                                : "gray",
                          }}
                        >
                          ↓
                        </span>
                      </span>
                    </th>

                    {/* Skills */}
                    <th
                      scope="col"
                      onClick={() => handleSort("skills")}
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      Skills
                      <span>
                        <span
                          style={{
                            color:
                              sortedColumn === "skills" && sortOrder === "asc"
                                ? "black"
                                : "gray",
                          }}
                        >
                          ↑
                        </span>{" "}
                        <span
                          style={{
                            color:
                              sortedColumn === "skills" && sortOrder === "desc"
                                ? "black"
                                : "gray",
                          }}
                        >
                          ↓
                        </span>
                      </span>
                    </th>

                    {/* Salary */}
                    <th
                      scope="col"
                      onClick={() => handleSort("salary")}
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      Salary
                      <span>
                        <span
                          style={{
                            color:
                              sortedColumn === "salary" && sortOrder === "asc"
                                ? "black"
                                : "gray",
                          }}
                        >
                          ↑
                        </span>{" "}
                        <span
                          style={{
                            color:
                              sortedColumn === "salary" && sortOrder === "desc"
                                ? "black"
                                : "gray",
                          }}
                        >
                          ↓
                        </span>
                      </span>
                    </th>

                    {/* Description */}
                    <th scope="col">Job Description</th>

                    {/* Action Headers */}
                    <th scope="col">Share</th>
                    <th scope="col">Applications</th>
                  </tr>
                </thead>

                <tbody>
                  {jobs.map((job) => (
                    <tr key={job.id}>
                      {/* Job Title */}
                      <td>
                        <a
                          onClick={() => handleViewSummary(job.jobsummary)}
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          {job.jobTitle}
                        </a>
                      </td>

                      {/* Job Type */}
                      <td>{job.jobType}</td>

                      {/* Skills */}
                      <td>{job.skills}</td>

                      {/* Salary */}
                      <td>{job.salary}</td>

                      {/* Summary */}
                      <td>
                        <Button
                          variant="secondary"
                          className="description btn-rounded"
                          onClick={() => handleViewSummary(job.jobsummary)}
                        >
                          Summary
                        </Button>
                      </td>

                      {/* Share */}
                      <td>
                        <Button
                          variant="success"
                          onClick={() => handleShare(job)}
                        >
                          Share
                        </Button>
                      </td>

                      {/* View Application */}
                      <td>
                        <Link
                          to="/hr-dashboard/hr-applications/view-applications"
                          onClick={(e) => {
                            e.preventDefault();

                            navigate(
                              "/hr-dashboard/hr-applications/view-applications",
                              {
                                state: {
                                  userName,
                                  userEmail,
                                  jobId: job.jobId,
                                  jobTitle: job.jobTitle,
                                  currentJobApplicationPage: page,
                                  currentJobApplicationPageSize: pageSize,
                                  companyName: job.companyName,
                                },
                              },
                            );
                          }}
                          className="nav-link"
                        >
                          <Button variant="primary" size="sm">View Apps</Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            <Pagination
              page={page}
              pageSize={pageSize}
              totalPages={totalPages}
              handlePageSizeChange={handlePageSizeChange}
              isPageSizeDisabled={isPageSizeDisabled}
              handlePageClick={handlePageClick}
            />
          </Card>
        </div>
      ) : (
        <div className="d-flex justify-content-center align-items-center">
          <span>No AI jobs available</span>
        </div>
      )}

      {/* Job Summary Modal */}
      {selectedJobSummary && (
        <div
          className="modal fade show"
          tabIndex="-1"
          role="dialog"
          style={{
            display: "block",
          }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Job Summary</h5>
              </div>

              <div
                className="modal-body"
                style={{
                  maxHeight: "60vh",
                  overflowY: "auto",
                }}
              >
                <pre className="job-details-text">{selectedJobSummary}</pre>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleCloseModal();
                    }
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIJobsByJobbox;
